import urljoin from 'urljoin'
import { GuillotinaClient, stringToSlug } from '@guillotinaweb/react-gmi'
import { RestClient } from '@guillotinaweb/react-gmi'

import { getFiltersByPath, rsplit } from '../utils'
import { getNLColumnHeadings } from '../guillotina/column-headings/newsletters'

const cacheCanIDo = {}
let cachePaises = []

export class AdminClient extends GuillotinaClient {
  async get(path) {
    const res = await super.get(path)

    if (!isSharingEndpoint(path)) {
      return res
    }

    return {
      ...res,
      ok: res.ok,
      status: res.status,
      json: async () => normalizeSharing(await res.json()),
    }
  }

  async getItems(path, start = 0, pageSize = 10) {
    const filters = getFiltersByPath(path)
    const depth = path.split('/').length
    const sort =
      path === '/newsletters/'
        ? { efective_date: { order: 'desc' } }
        : path === '/home/'
        ? { date_start: { order: 'desc' } }
        : ['id']

    const result = await this.esQuery({
      ...Elastic.getItems(path, depth, start, pageSize, filters),
      sort,
    })
    return await result.json()
  }

  async getTypeSchema(path, name) {
    const res = await this.rest.get(`@types/${name}`)
    return res.json()
  }

  async translate(item, id_tipo) {
    const res = await this.rest.request('/@translate_content', {
      method: 'POST',
      body: JSON.stringify({
        text: item,
        target_lang: ['en', 'de', 'fr', 'it'],
        id_tipo: id_tipo,
      }),
    })
    return res.json()
  }

  getItemsColumn(items, path) {
    const column = super.getItemsColumn(items, path)

    if (path === '/newsletters/') {
      return getNLColumnHeadings(column)
    }

    return column
  }

  async upsert(path, body) {
    if (path.endsWith('/')) path = path.replace(/\/$/, '')

    let [initpath, id] = rsplit(path, '/', 1)
    return await this.rest.post(urljoin(initpath, '@upsert'), { ...body, id })
  }

  lock(path) {
    return this.rest
      .post(`${path.replace(/(^\/|\/$)/g, '')}/@lock`)
      .then((r) => r.json())
      .catch(() => ({}))
  }

  unlock(path) {
    return this.rest.post(`${path.replace(/(^\/|\/$)/g, '')}/@unlock`)
  }

  patch(path, item) {
    const key = `last-mod-date:${path}`
    const lastModDate = sessionStorage.getItem(key)

    return this.rest
      .request(
        path,
        {
          method: 'PATCH',
          body: JSON.stringify(item),
        },
        lastModDate
          ? { ...this.rest.getHeaders(), 'last-mod-date': lastModDate }
          : this.rest.getHeaders()
      )
      .then(async (r) => {
        // @todo remove this "get" in the future (the patch should return
        // the modification_date).
        const res = await this.rest.get(path).then((r) => r.json())
        sessionStorage.setItem(key, res.modification_date)
        return r
      })
      .catch(() => ({}))
  }

  async upload(path, file, folder, field = null) {
    // console.log({ path, file, folder, field })

    // Remove folder trailing slash
    const folderWithoutTrailingSlash = folder.replace(/\/$/, '')
    const name = file.name || file.filename
    // take .png, .jpg, etc
    const [format] = name.match(/\.([a-z]|[0-9])+$/g) || ['']
    // Remove bad characters + add again the format
    const filename = stringToSlug(name.replace(format, '')) + format

    const headers = this.rest.getHeaders()
    delete headers['Content-Type']
    headers['Content-Type'] = file['content-type']
    headers['X-UPLOAD-FILENAME'] = `${folderWithoutTrailingSlash}/${filename}`
    if (field === null) field = `files/${filename}`
    return await this.rest.request(
      urljoin(path, '@upload', field),
      {
        method: 'PATCH',
        body: file.data,
      },
      headers
    )
  }

  async deleteFile(path, field) {
    const url = urljoin(
      path.replace(/^\//, ''),
      '@delete',
      field.replace(/^\//, '')
    )
    return await this.rest.delete(url)
  }

  async search(
    path,
    params,
    container = false,
    prepare = true,
    start = 0,
    pageSize = 10
  ) {
    const level = path.split('/').length
    const query = Elastic.getItems(path, level, start, pageSize)
    query.query.bool.filter[1] = { range: { depth: { gte: level } } }

    // @TODO this is a bit hacky, but will refactor on future
    if (params.startsWith('title__in=')) {
      let qs = params.replace('title__in', '')
      query.query.bool.should = [
        {
          match: {
            title: {
              query: qs,
              fuzziness: 'AUTO',
              max_expansions: 15,
            },
          },
        },
        {
          match_phrase_prefix: {
            title: {
              query: qs,
              boost: 2,
            },
          },
        },
      ]
      query.query.bool.minimum_should_match = 1
    } else {
      query.query.bool.must = { match: { ...parseParams(params) } }
    }
    const res = await this.esQuery(query)
    return await res.json()
  }

  async esQuery(query) {
    const result = await this.rest.post('@search', query)
    return result
  }

  async getProducts(query, lang = null) {
    const result = await this.rest.post(
      lang !== null
        ? `@search-aggs-product?lang=${lang}`
        : '@search-aggs-product',
      query
    )
    return [result.status, await result.json()]
  }

  async prodByReferencia(referencias) {
    const result = await this.rest.post('@prod-by-referencia', { referencias })
    return await result.json()
  }

  countNewsletter(id) {
    return this.rest.get(`newsletters/${id}/@count`).then((r) => r.json())
  }

  progressNL(id) {
    return this.rest.get(`newsletters/${id}/@progress`).then((r) => r.json())
  }

  scheduleNewsletter(id, unschedule = false) {
    const endpoint = unschedule ? '@unschedule' : '@schedule'
    return this.rest.post(`newsletters/${id}/${endpoint}`).then((r) => r.json())
  }

  uploadToBrevo(id) {
    return this.rest
      .post(`newsletters/${id}/@upload-to-brevo`)
      .then((r) => r.json())
  }

  testEmailNL(id, body) {
    return this.rest.post(`newsletters/${id}/@send-test-email`, body)
  }

  verifyNLToggle(id, verify = true) {
    const endpoint = verify ? '@mark-as-verified' : '@mark-as-draft'
    return this.rest.post(`newsletters/${id}/${endpoint}`)
  }

  getColectivos(id) {
    return this.rest
      .get(`newsletters/${id}/@getColectivos`)
      .then((r) => r.json())
  }

  async getProductsRegion(region) {
    /*
     * Search for products in that region or it's subregions
     */
    const result = await this.rest.get(region + '/@products')
    return await result.json()
  }

  async getGroups() {
    return {
      json: async () => [],
    }
  }

  async getRoles() {
    return {
      json: async () => [],
    }
  }

  async delete(path, data) {
    if (path.startsWith('http')) {
      const url = new URL(path)
      const parts = url.pathname.split('/').slice(3).join('/')
      return await this.rest.delete(`${parts}`, data)
    }
    return await this.rest.delete(path, data)
  }

  // A cached implementation that at some point could be opensourced
  async canido(path, permissions) {
    // Hardcoded permissions needed in admin
    permissions = [
      ...permissions,
      'vinissimus.ViewLogs',
      'vinissimus.ScheduleNewsletters',
      'vinissimus.ReviewContent',
    ]

    if (path.startsWith('/')) {
      path = path.slice(1)
    }
    if (Array.isArray(permissions)) {
      permissions.join(',')
    }

    const key = `${path}/${permissions}`
    if (!cacheCanIDo[key]) {
      const url = `${path}@canido?permissions=${permissions}`
      const res = await this.rest.get(url)
      if (res.status === 200) {
        cacheCanIDo[key] = await res.json()
      } else {
        return res
      }
    }
    return {
      json: async () => cacheCanIDo[key],
    }
  }

  async getTypes(path) {
    if (path === '/tags/') {
      return ['Tag']
    }

    if (path === '/bodegas/') {
      return ['Bodega']
    }

    if (path === '/home/') {
      return ['Home']
    }

    if (path === '/newsletters/') {
      return ['Newsletter']
    }

    if (path === '/promos/') {
      return ['Promos']
    }

    if (path === '/landings/') {
      return ['Landing']
    }

    if (path === '/packs/') {
      return ['Producto']
    }

    if (path === '/taxonomies/') {
      return ['Taxonomy']
    }

    if (path.startsWith('/bodegas/')) {
      // we dont want to be able to create anything inside bodegas
      if (path.split('/').length > 4) {
        return []
      }
      return ['Producto']
    }

    if (path.startsWith('/tags/')) {
      return ['Tag']
    }
    return await super.getTypes(path)
  }

  async getPaises() {
    if (cachePaises.length > 0) {
      return cachePaises
    }
    const req = await fetch('/v2/@constants')
    let data = await req.json()
    cachePaises = data.countries.map((item) => ({
      text: item.nombre,
      value: item.id_pais,
    }))
    return cachePaises
  }
}

export function getClient(url, auth) {
  return new AdminClient(new RestClient(url, auth))
}

const baseSharing = {
  local: {
    roleperm: {},
    prinperm: {},
    prinrole: {},
  },
  inherit: {},
}

const isSharingEndpoint = (path) =>
  path.replace(/\?.*$/, '').replace(/\/$/, '').endsWith('@sharing')

const objectOrEmpty = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}

const normalizeSharing = (sharing) => {
  const local = objectOrEmpty(sharing?.local)

  return {
    ...objectOrEmpty(sharing),
    local: {
      roleperm: objectOrEmpty(local.roleperm),
      prinperm: objectOrEmpty(local.prinperm),
      prinrole: objectOrEmpty(local.prinrole),
    },
    inherit: objectOrEmpty(sharing?.inherit),
  }
}

const parseParams = (params) => {
  const parser = new URLSearchParams(params)
  const result = {}
  parser.forEach((val, key) => {
    const [field] = key.split('__')
    // result[field] = val
    result[field] = { query: val }
  })
  return result
}

const Elastic = {
  getItems: (path, depth, start, size, filters = []) => {
    return {
      ...Elastic.query({
        filter: [{ prefix: { path } }, { term: { depth } }, ...filters],
      }),
      from: start,
      size,
    }
  },

  query: (params) => ({ query: { bool: params } }),
}
